import { useMemo } from 'react';

import InputField from '../Forms/Input/InputField';
import SelectField from '../Forms/Input/SelectField';
import FormModal from '../Modals/FormModal';
import ModalActionButtons from '../Modals/Buttons/ModalActionButtons';

import { useCashAdjustment } from '../../hooks/cash/useCashAdjustment';
import { useCashSession } from '../../hooks/cash/useCashSession';

import CashSessionInfoCard from '../CashStatus/CashSessionInfoCard';
import CashAdjustmentPreview from './CashAdjustmentPreview';
import QuickAmountButtons from '../Buttons/QuickAmountButtons';

const quickAmounts = [10, 20, 50, 100, 200, 500];

const adjustmentTypeOptions = [
  { value: 'INCREASE', label: 'Aumentar caja (+)' },
  { value: 'DECREASE', label: 'Disminuir caja (-)' },
];

const CashAdjustmentModal = () => {
  const { form, showModal, updateField, adjustment, close } =
    useCashAdjustment();
  const { activeSession } = useCashSession();

  /**
   * 🔢 Monto numérico
   */
  const amount = useMemo(() => {
    return parseFloat(String(form.amount));
  }, [form.amount]);

  /**
   * 🧠 Validación
   */
  const isInvalidAmount = isNaN(amount) || amount === 0;

  /**
   * ⚖️ Registrar ajuste
   */
  const handleSubmit = async () => {
    if (!activeSession) return;

    if (isInvalidAmount) {
      alert('Ingrese un monto válido');
      return;
    }

    await adjustment({
      ...form,
      amount,
      sessionId: activeSession.id,
    });
  };

  return (
    <FormModal
      isOpen={showModal}
      onClose={close}
      title="Registrar ajuste de caja"
      maxWidth="max-w-5xl"
      overlayBg="black"
      zIndex="front"
    >
      <div className="space-y-4">
        {/* GRID PRINCIPAL */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr]">
          {/* FORMULARIO */}
          <div className="space-y-5">
            <SelectField
              label="Tipo de ajuste"
              name="adjustmentType"
              iconKey="calculator"
              value={form.adjustmentType || ''}
              options={adjustmentTypeOptions}
              placeholder="Seleccione el tipo de ajuste"
              onChange={(e) => updateField('adjustmentType', e.target.value)}
            />

            <InputField
              label="Monto del ajuste"
              name="amount"
              type="number"
              step="0.01"
              iconKey="dollar"
              value={form.amount || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('amount', e.target.value)
              }
            />

            {/* ATAJOS DE MONTO */}
            <QuickAmountButtons
              amounts={quickAmounts}
              selected={Math.abs(Number(form.amount))}
              onSelect={(value) => updateField('amount', value.toFixed(2))}
            />

            <InputField
              label="Descripción"
              name="description"
              iconKey="document"
              type="text"
              placeholder="Motivo del ajuste"
              value={form.description || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('description', e.target.value)
              }
            />
          </div>

          {/* CONTEXTO DE CAJA */}
          <div className="space-y-4">
            {activeSession && <CashSessionInfoCard />}

            <CashAdjustmentPreview
              currentBalance={activeSession?.currentBalance || 0}
              amount={amount || 0}
              adjustmentType={form.adjustmentType}
            />
          </div>
        </div>

        {/* ACCIONES */}
        <ModalActionButtons
          onCancel={close}
          onConfirm={handleSubmit}
          confirmLabel="Registrar ajuste"
          confirmColor="warning"
          disabled={isInvalidAmount}
        />
      </div>
    </FormModal>
  );
};

export default CashAdjustmentModal;
